import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import "../../css/StyleJewellery/style_cart.css";

import { ConfirmOrder } from "../../api/styleJewelleryService";

const DEFAULT_IMAGE = "/assets/images/VaultAccess/no-image3.png";

const STEPS = [
    { label: "Checkout", icon: "pi-shopping-cart" },
    { label: "Shipping", icon: "pi-map-marker" },
    { label: "Review & Confirm", icon: "pi-check-square" },
    { label: "Order Confirmation", icon: "pi-verified" }
];

const CheckoutStepper = ({ currentStep }) => {
    return (
        <div className="checkout-stepper-container">
            <div className="checkout-stepper">
                {STEPS.map((step, index) => (
                    <div
                        key={index}
                        className={`step-item ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''} ${(index === currentStep && index === STEPS.length - 1) ? 'final-step' : ''}`}
                    >
                        <div className="step-marker">
                            {index < currentStep ? <i className="pi pi-check" /> : index + 1}
                        </div>
                        <div className="step-label">{step.label}</div>
                        <div className="step-line" />
                    </div>
                ))}
            </div>
        </div>
    );
};

const Checkout = () => {
    document.title = "Checkout | Meera Jewellery House Admin";
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const { selectedItems = [], checkoutData = [], checkoutSummary = {} } = location.state || {};
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [orderNo, setOrderNo] = useState("");
    const [shippingInfo, setShippingInfo] = useState({
        firstName: user?.SelectedCustomer_Name?.split(' ')[0] || "",
        lastName: user?.SelectedCustomer_Name?.split(' ')?.slice(1)?.join(' ') || "",
        email: user?.SelectedCustomer_Email || "",
        phone: user?.SelectedCustomer_Phone || "",
        address: user?.SelectedCustomer_Address || "",
        city: user?.SelectedCustomer_City || "",
        state: user?.SelectedCustomer_StateName || "",
        pincode: user?.SelectedCustomer_ZipCode || "",
        country: user?.SelectedCustomer_CountryName || ""
    });

    React.useEffect(() => {
        if (checkoutSummary && (checkoutSummary.Shipping || checkoutSummary.Contact)) {
            const fullAddress = checkoutSummary.Shipping || "";

            let parsedAddress = "";
            let parsedCity = "";
            let parsedState = "";
            let parsedPincode = "";
            let parsedCountry = "";

            if (fullAddress.includes(',')) {
                const parts = fullAddress.split(',').map(p => p.trim());
                if (parts.length >= 4) {
                    parsedAddress = parts.slice(0, parts.length - 3).join(', ');
                    parsedCity = parts[parts.length - 3];
                    const stateZip = parts[parts.length - 2];
                    parsedCountry = parts[parts.length - 1];

                    if (stateZip.includes('-')) {
                        [parsedState, parsedPincode] = stateZip.split('-').map(s => s.trim());
                    } else {
                        parsedState = stateZip;
                    }
                } else if (parts.length === 3) {
                    parsedAddress = parts[0];
                    parsedCity = parts[1];
                    const stateZip = parts[2];
                    if (stateZip.includes('-')) {
                        [parsedState, parsedPincode] = stateZip.split('-').map(s => s.trim());
                    } else {
                        parsedState = stateZip;
                    }
                }
            }

            setShippingInfo(prev => ({
                ...prev,
                address: prev.address || parsedAddress || fullAddress,
                city: prev.city || parsedCity,
                state: prev.state || parsedState,
                pincode: prev.pincode || parsedPincode,
                country: prev.country || parsedCountry,
                phone: prev.phone || checkoutSummary.Contact
            }));
        }
    }, [checkoutSummary]);

    const displayItems = checkoutData.length > 0 ? checkoutData : selectedItems;

    const formatPrice = (price) => {
        let numericPrice = 0;
        if (typeof price === "number") {
            numericPrice = price;
        } else if (typeof price === "string") {
            numericPrice = parseFloat(price.replace(/[^\d.]/g, ""));
        }

        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(numericPrice || 0).replace('₹', 'Rs. ');
    };

    const calculateTotal = () => {
        return displayItems.reduce((acc, item) => {
            let price = 0;
            const rawValue = item.TotalAmount || item.LineAmount;

            if (rawValue !== null && rawValue !== undefined) {
                if (typeof rawValue === 'number') {
                    price = rawValue;
                } else {
                    const cleanValue = rawValue.toString().replace(/[^\d.]/g, "");
                    price = parseFloat(cleanValue);
                }
            }
            return acc + (isNaN(price) ? 0 : price);
        }, 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setShippingInfo(prev => ({ ...prev, [name]: value }));
    };

    const handleConfirmOrder = async () => {
        setLoading(true);
        try {
            const payload = {
                Party_ID: user?.SelectedCustomer_ID,
                Enquiry_ID: "",
                EnquiryNo: "",
                Status: "",
                StatusComment: "",
                StyleCount: "",
                Items: displayItems.map(x => ({
                    Style_ID: x.Style_ID,
                    StyleNo: x.StyleNo,
                    JewelCode: x.JewelCode,
                    Cart_ID: x.Cart_ID.toString() ?? "",
                })),
                AddressXmlStr: `${shippingInfo.address}, ${shippingInfo.city}, ${shippingInfo.state} - ${shippingInfo.pincode}, ${shippingInfo.country}`
            };

            const result = await ConfirmOrder(payload);
            const response = result?.Message?.[0];
            console.log("API Response:", result);
            if (response?.RETURNCODE == 1) {
                setOrderNo(result?.OrderNo || "");
                setCurrentStep(3);
            } else {
                console.log(response?.RETURNMESSAGE || "Order failed");
            }
        } catch (error) {
            console.error("Error confirming order:", error);
            console.log("An error occurred while confirming your order.");
        } finally {
            setLoading(false);
        }
    };

    const nextStep = () => {
        if (currentStep === 2) {
            handleConfirmOrder();
        } else {
            setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
        }
    };
    const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 0));

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
        exit: { opacity: 0, x: -20, transition: { duration: 0.2 } }
    };

    if (currentStep === 3) {
        return (
            <div className="style-cart-page checkout-page">
                <CheckoutStepper currentStep={currentStep} />
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="confirmation-card"
                >
                    <div className="success-icon-wrapper">
                        <i className="pi pi-check-circle" />
                    </div>
                    <h1 className="thank-you-title">Thank You!</h1>
                    <div className="order-number-badge">Order #{orderNo || `MJH-${Math.floor(100000 + Math.random() * 900000)}`}</div>
                    <p className="confirmation-message">
                        Your order has been placed successfully. We've sent a confirmation email to <b>{shippingInfo.email}</b> with your order details.
                    </p>
                    <div className="confirmation-actions">
                        <Link to="/dashboard" className="btn-primary">
                            Go to Dashboard
                        </Link>
                        <Link to="/StyleInventory" className="btn-secondary" style={{ background: '#f1f5f9', color: '#475569', padding: '8px 24px', borderRadius: '8px', fontWeight: '600', textDecoration: 'none' }}>
                            Continue Shopping
                        </Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="style-cart-page checkout-page">
            <div className="cart-header-card">
                <div className="cart-header-top-row">
                    <h1 className="cart-page-title">
                        {currentStep === 0 ? "Checkout Summary" : currentStep === 1 ? "Shipping Details" : "Review Your Order"}
                        <span>({displayItems.length} items)</span>
                    </h1>
                    <button className="back-btn" onClick={currentStep === 0 ? () => navigate(-1) : prevStep} aria-label="Go back">
                        <i className="pi pi-arrow-left"></i>
                    </button>
                </div>
            </div>

            <CheckoutStepper currentStep={currentStep} />

            <div className="cart-layout">
                <div className="cart-main-section">
                    <AnimatePresence mode="wait">
                        {currentStep === 0 && (
                            <motion.div
                                key="step0"
                                initial="hidden"
                                animate="show"
                                exit="exit"
                                variants={itemVariants}
                                className="cart-items-list-exact"
                            >
                                <div className="customer-items-list">
                                    {displayItems.map((item, index) => (
                                        <div key={item.Cart_ID || index} className="cart-item-exact">
                                            <div className="item-left-visual">
                                                <img src={item.Image1 || DEFAULT_IMAGE} alt={item.Shortdescription} onError={(e) => { e.target.src = DEFAULT_IMAGE; }} />
                                            </div>
                                            <div className="item-details-container">
                                                <div className="item-info-col">
                                                    <h2 className="item-title-exact">{item.Shortdescription || "Untitled Design"}</h2>
                                                    <div className="item-ref-code">Jewel Code: <span>{item.JewelCode}</span></div>
                                                    <div className="item-ref-code">Style No: <span>{item.StyleNo}</span></div>
                                                    <div className="item-clock-date">
                                                        <i className="pi pi-clock" style={{ marginRight: '5px' }} />
                                                        {formatDate(item.InsertDatetime || item.CartEntryDate)}
                                                    </div>
                                                </div>
                                                <div className="spec-column">
                                                    <div className="spec-title-exact">Product Details</div>
                                                    <div className="spec-row-exact"><span className="spec-label-exact">Purity</span><span className="spec-value-exact">{item.MetalQuality || "-"}</span></div>
                                                    <div className="spec-row-exact"><span className="spec-label-exact">Gold Wt</span><span className="spec-value-exact">{item.TotalNetWt ? `${item.TotalNetWt} g` : "-"}</span></div>
                                                    <div className="spec-row-exact"><span className="spec-label-exact">Dimension</span><span className="spec-value-exact">{item.ItemSize || "-"}</span></div>
                                                </div>
                                                <div className="spec-column">
                                                    <div className="spec-title-exact">{item.CDiaWt ? "Stone Specs" : "Diamond Details"}</div>
                                                    <div className="spec-row-exact">
                                                        <span className="spec-label-exact">Weight</span>
                                                        <span className="spec-value-exact">{item.DiaPcs || 0} Pcs / {item.DiaWt || 0} Cts</span>
                                                    </div>
                                                    <div className="spec-row-exact"><span className="spec-label-exact">Gross Wt</span><span className="spec-value-exact">{item.GrossWt ? `${item.GrossWt} g` : "-"}</span></div>
                                                    <div className="spec-row-exact"><span className="spec-label-exact">Color</span><span className="spec-value-exact">{item.MetalTone || "-"}</span></div>
                                                </div>
                                                <div className="item-actions-col">
                                                    <div className="item-price-exact fs-4">
                                                        {typeof item.LineAmount === 'string' ? item.LineAmount : formatPrice(item.TotalAmount || item.LineAmount)}
                                                    </div>
                                                    <div className="mt-2" style={{ color: '#666', fontSize: '0.9rem' }}>
                                                        Quantity: <b>{item.Qty || 1}</b>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {currentStep === 1 && (
                            <motion.div
                                key="step1"
                                initial="hidden"
                                animate="show"
                                exit="exit"
                                variants={itemVariants}
                                className="shipping-section-card"
                            >
                                <h2 className="section-title-premium">
                                    <i className="pi pi-map-marker" style={{ color: '#002347' }} />
                                    Shipping Information
                                </h2>
                                <div className="shipping-form-grid">
                                    <div className="form-group-premium">
                                        <label>First Name</label>
                                        <input type="text" name="firstName" value={shippingInfo.firstName} readOnly className="input-premium readonly" placeholder="First name" />
                                    </div>
                                    <div className="form-group-premium">
                                        <label>Last Name</label>
                                        <input type="text" name="lastName" value={shippingInfo.lastName} readOnly className="input-premium readonly" placeholder="Last name" />
                                    </div>
                                    <div className="form-group-premium">
                                        <label>Email Address</label>
                                        <input type="email" name="email" value={shippingInfo.email} readOnly className="input-premium readonly" placeholder="Email" />
                                    </div>
                                    <div className="form-group-premium">
                                        <label>Phone Number</label>
                                        <input type="tel" name="phone" value={shippingInfo.phone} readOnly className="input-premium readonly" placeholder="Phone" />
                                    </div>
                                    <div className="form-group-premium full-width">
                                        <label>Street Address</label>
                                        <input type="text" name="address" value={shippingInfo.address} readOnly className="input-premium readonly" placeholder="House number and street name" />
                                    </div>
                                    <div className="form-group-premium">
                                        <label>Country</label>
                                        <input type="text" name="country" value={shippingInfo.country} readOnly className="input-premium readonly" placeholder="Country" />
                                    </div>
                                    <div className="form-group-premium">
                                        <label>State / Province</label>
                                        <input type="text" name="state" value={shippingInfo.state} readOnly className="input-premium readonly" placeholder="State" />
                                    </div>
                                    <div className="form-group-premium">
                                        <label>City</label>
                                        <input type="text" name="city" value={shippingInfo.city} readOnly className="input-premium readonly" placeholder="City" />
                                    </div>
                                    <div className="form-group-premium">
                                        <label>ZIP Code</label>
                                        <input type="text" name="pincode" value={shippingInfo.pincode} readOnly className="input-premium readonly" placeholder="ZIPCode" />
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {currentStep === 2 && (
                            <motion.div
                                key="step2"
                                initial="hidden"
                                animate="show"
                                exit="exit"
                                variants={itemVariants}
                                className="review-section"
                            >
                                <div className="cart-items-list-exact">
                                    <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#002347', marginBottom: '1rem' }}>Order Items</h3>
                                    <div className="cart-items col-12">
                                        {displayItems.map((item, index) => (
                                            <div key={item.Cart_ID || index} className="cart-item-exact mini col-lg-6" style={{ marginBottom: '10px', height: '120px' }}>
                                                <div className="item-left-visual" style={{ width: '100px', minWidth: '100px', height: '100px' }}>
                                                    <img src={item.Image1 || DEFAULT_IMAGE} alt={item.Line1} style={{ padding: '8px' }} />
                                                </div>
                                                <div className="item-details-container" style={{ gridTemplateColumns: '1fr auto', padding: '10px' }}>
                                                    <div>
                                                        <div style={{ fontWeight: '600', color: '#002347' }}>{item.Shortdescription || item.StyleNo}</div>
                                                        <div style={{ fontSize: '0.75rem', color: '#64748b', display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '4px' }}>
                                                            <span>Gold: {item.TotalNetWt}g</span>
                                                            <span>Dia: {item.DiaWt}ct / {item.DiaPcs}pcs</span>
                                                            <span>Gross: {item.GrossWt}g</span>
                                                        </div>
                                                        <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '4px' }}>Qty: {item.Qty || 1}</div>
                                                    </div>
                                                    <div style={{ fontWeight: '700', color: '#002347' }}>
                                                        {typeof item.LineAmount === 'string' ? item.LineAmount : formatPrice(item.TotalAmount || item.LineAmount)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="review-info-grid">
                                    <div className="review-card-item">
                                        <div className="review-card-title">
                                            Shipping Address
                                        </div>
                                        <div className="review-card-content">
                                            <b>{shippingInfo.firstName} {shippingInfo.lastName}</b><br />
                                            {shippingInfo.address}<br />
                                            {shippingInfo.city}, {shippingInfo.state} - {shippingInfo.pincode}<br />
                                            {shippingInfo.country && <>{shippingInfo.country}<br /></>}
                                            Phone: {shippingInfo.phone}
                                        </div>
                                    </div>
                                    <div className="review-card-item">
                                        <div className="review-card-title">Contact Info</div>
                                        <div className="review-card-content">
                                            Email: {shippingInfo.email}<br />
                                            Phone: {shippingInfo.phone}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <aside className="summary-sidebar">
                    <h2 className="summary-title">Order Summary</h2>
                    <div className="summary-row">
                        <span className="spec-label-exact">Total Items</span>
                        <span className="spec-value-exact">{displayItems.length}</span>
                    </div>
                    <div className="summary-row">
                        <span className="spec-label-exact">Subtotal</span>
                        <span className="spec-value-exact">{formatPrice(calculateTotal())}</span>
                    </div>
                    <div className="summary-row">
                        <span className="spec-label-exact">Estimated Tax</span>
                        <span className="spec-value-exact">Included</span>
                    </div>
                    <div className="summary-row total">
                        <span className="spec-label-exact">Total Amount</span>
                        <span>{formatPrice(calculateTotal())}</span>
                    </div>

                    <div className="checkout-btn-wrapper mt-4">
                        <button
                            className="btn-primary w-100"
                            onClick={nextStep}
                            disabled={loading}
                        >
                            {loading ? <i className="pi pi-spin pi-spinner" style={{ marginRight: '8px' }} /> : null}
                            {currentStep === 0 ? "Continue to Shipping" : currentStep === 1 ? "Continue to Review" : "Confirm Order"}
                        </button>

                        <button
                            className="back-step-btn"
                            onClick={currentStep === 0 ? () => navigate(-1) : prevStep}
                        >
                            <i className="pi pi-arrow-left" />
                            Back
                        </button>
                    </div>

                    {currentStep < 2 && (
                        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                            <i className="pi pi-shield" style={{ color: '#10b981', marginRight: '8px' }} />
                            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Secure Checkout</span>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
};

export default Checkout;
